import React from 'react';
import Typography from '@mui/material/Typography';
import { QuotationServiceSimple } from '../../types';
import { Box, Chip, Container } from '@mui/material';
import { getCityNameCleaned } from '../../helpers';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
/**
 * Lo mínimo que necesita del destino: subconjunto estructural tanto de
 * DestinationSummaryForDestinationMultiple (multi) como de DestinationSimple,
 * así el recap sirve en los dos drawers de "Tu selección".
 */
export interface IncludeRecapDestination {
	cityCode: string;
	cityName: string;
	nights: number;
	services: QuotationServiceSimple[];
}

interface Props {
	destinations: IncludeRecapDestination[];
	manualServices: QuotationServiceSimple[];
	hasFlight: boolean;
}

export const IncludeMultiDestination = ({ destinations, manualServices, hasFlight }: Props): JSX.Element => {
	const getTotalNights = (destinationCode: string) =>
		destinations.filter((x) => x.cityCode === destinationCode).reduce((a, b) => a + b.nights, 0);
	const uniqueDestinations = destinations.filter(
		(destination, index) => destinations.findIndex((x) => x.cityCode === destination.cityCode) === index,
	);
	const allServices = destinations.flatMap((destination) => destination.services).concat(manualServices);
	const transferServices = allServices.filter((service) => service.name.toLowerCase().includes('traslado'));
	const otherServices = allServices.filter((service) => !service.name.toLowerCase().includes('traslado'));

	return (
		<Container disableGutters sx={{ overflow: 'auto', my: 2 }}>
			<Box display="grid" gap={1}>
				{hasFlight && (
					<Box
						display="flex"
						gap={1.25}
						alignItems="flex-start"
						p={1.25}
						borderRadius="8px"
						bgcolor="rgba(49, 25, 75, 0.035)"
					>
						<FlightTakeoffIcon color="secondary" sx={{ fontSize: 20, mt: 0.1 }} />
						<Box>
							<Typography variant="body2" fontWeight={800} color="primary">
								Vuelos
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Boleto aéreo con tasas e impuestos incluidos.
							</Typography>
						</Box>
					</Box>
				)}
				<Box
					display="flex"
					gap={1.25}
					alignItems="flex-start"
					p={1.25}
					borderRadius="8px"
					bgcolor="rgba(49, 25, 75, 0.035)"
				>
					<HotelIcon color="secondary" sx={{ fontSize: 20, mt: 0.1 }} />
					<Box>
						<Typography variant="body2" fontWeight={800} color="primary">
							Alojamiento
						</Typography>
						<Box display="flex" flexWrap="wrap" gap={0.75} mt={0.75}>
							{uniqueDestinations.map((destination, index) => (
								<Chip
									key={`included-nights-${destination.cityCode}-${index}`}
									size="small"
									label={`${getTotalNights(destination.cityCode)} noches en ${getCityNameCleaned(
										destination.cityName,
									)}`}
									sx={{ borderRadius: '999px', fontWeight: 700, bgcolor: 'white' }}
								/>
							))}
						</Box>
					</Box>
				</Box>
				{transferServices.length > 0 && (
					<Box
						display="flex"
						gap={1.25}
						alignItems="flex-start"
						p={1.25}
						borderRadius="8px"
						bgcolor="rgba(237, 102, 2, 0.055)"
					>
						<DirectionsCarIcon color="secondary" sx={{ fontSize: 20, mt: 0.1 }} />
						<Box>
							<Typography variant="body2" fontWeight={800} color="primary">
								Traslados incluidos
							</Typography>
							<Box display="grid" gap={0.45} mt={0.5}>
								{transferServices.map((service, index) => (
									<Box key={`transfer-included-${index}`} display="flex" alignItems="flex-start" gap={0.75}>
										<CheckCircleIcon color="secondary" sx={{ fontSize: 15, mt: 0.2 }} />
										<Typography variant="caption" color="text.secondary" lineHeight={1.25}>
											<strong>{service.name}</strong>
										</Typography>
									</Box>
								))}
							</Box>
						</Box>
					</Box>
				)}
				{otherServices.length > 0 && (
					<Box
						display="flex"
						gap={1.25}
						alignItems="flex-start"
						p={1.25}
						borderRadius="8px"
						bgcolor="rgba(49, 25, 75, 0.035)"
					>
						<CheckCircleIcon color="secondary" sx={{ fontSize: 20, mt: 0.1 }} />
						<Box>
							<Typography variant="body2" fontWeight={800} color="primary">
								Servicios adicionales incluidos
							</Typography>
							<Box display="flex" gap={0.75} mt={0.75} flexWrap="wrap">
								{otherServices.map((service, index) => (
									<Chip
										key={`other-included-${index}`}
										size="small"
										label={service.name}
										sx={{
											borderRadius: '999px',
											fontWeight: 800,
											bgcolor: 'white',
											color: 'primary.main',
											maxWidth: '100%',
										}}
									/>
								))}
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</Container>
	);
};
